import { Button, CommonModalProps, TextBox } from "@animech/ui";
import {
	FunctionComponent,
	HTMLAttributes,
	KeyboardEvent,
	useCallback,
	useMemo,
	useState,
} from "react";
import * as Styled from "./send-email-modal.style";
import { EmailIcon, useUiData } from "src/ui";
import { Term } from "@definitions";
import { useEmail } from "src/api";
import { ModalBase } from "src/ui/components/modals/modal-base";

type SendemailModalProps = CommonModalProps<HTMLAttributes<HTMLDivElement>>;

type SendEmailModalTermKeys =
	| "title"
	| "textRow1"
	| "textRow2"
	| "toLabel"
	| "messageLabel"
	| "confirm"
	| "cancel";
const terms: { [key in SendEmailModalTermKeys]: Term } = {
	title: "sendEmailModal:title",
	textRow1: "sendEmailModal:text-row-1",
	textRow2: "sendEmailModal:text-row-2",
	toLabel: "sendEmailModal:to-label",
	messageLabel: "sendEmailModal:message-label",
	confirm: "sendEmailModal:confirm",
	cancel: "sendEmailModal:cancel",
};

export const SendEmailModal: FunctionComponent<SendemailModalProps> = (
	baseProps,
) => {
	const { onShouldClose } = baseProps;
	const { localizer } = useUiData();
	const [email, setEmail] = useState<string>();
	const [message, setMessage] = useState<string>();
	const [isLoading, setIsLoading] = useState<boolean>();
	const sendEmail = useEmail();

	const onSend = useCallback(async () => {
		if (!email || !message || isLoading) {
			return;
		}
		setIsLoading(true);
		await sendEmail(email, message);
		setIsLoading(false);

		onShouldClose?.();
	}, [email, isLoading, message, onShouldClose, sendEmail]);

	const onInputKeyDown = useCallback(
		(event: KeyboardEvent<HTMLInputElement>) => {
			if (event.key === "Enter") {
				onSend();
			}
		},
		[onSend],
	);

	const onEmailInputChange = (text: string) => {
		setEmail(text);
	};
	const onMessageInputChange = (text: string) => {
		setMessage(text);
	};

	const footerButtons = useMemo(
		() => [
			<Button
				key="send-button"
				onClick={onSend}
				disabled={!email}
				busy={isLoading}
			>
				{localizer.localizeTerm(terms.confirm)}
			</Button>,
		],
		[email, isLoading, localizer, onSend],
	);

	return (
		<ModalBase
			icon={<EmailIcon />}
			title={localizer.localizeTerm(terms.title)}
			footerButtons={footerButtons}
			{...baseProps}
		>
			<Styled.SendEmailModalContent>
				<Styled.Text>
					{localizer.localizeTerm(terms.textRow1)}
					<br />
					{localizer.localizeTerm(terms.textRow2)}
				</Styled.Text>

				<Styled.Text>{localizer.localizeTerm(terms.toLabel)}</Styled.Text>
				<TextBox
					appearance="ghost"
					color="neutral"
					value={email}
					onValueChange={onEmailInputChange}
					required
					type="email"
					instantValueChange
					inputAttributes={{ onKeyDown: onInputKeyDown }}
				/>

				<Styled.Text>{localizer.localizeTerm(terms.messageLabel)}</Styled.Text>
				<TextBox
					appearance="ghost"
					color="neutral"
					value={message}
					onValueChange={onMessageInputChange}
					required
					type="text"
					instantValueChange
					inputAttributes={{ onKeyDown: onInputKeyDown }}
				/>
			</Styled.SendEmailModalContent>
		</ModalBase>
	);
};
